import { useState, useEffect, useRef, useMemo } from "react";
import { METRICS_EVENTS } from "@cede/types";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useFiltersStore, CAMPAIGN_SEARCH_QUERY_MAX_LENGTH } from "@/store/filtersStore";
import {
  DEBOUNCE_DELAY,
  sanitizeSearchQuery,
  hasInvalidCharacters,
  exceedsMaxLength,
  getValidationError,
} from "@/utils/campaign";

/**
 * Custom hook for managing campaign search input state and validation
 *
 * Handles:
 * - Local search query state with sync to global store
 * - Real-time validation and sanitization
 * - Debounced search updates
 * - Analytics tracking
 *
 * @returns Search state and handlers
 */
export const useCampaignSearch = () => {
  const { searchQuery, setSearchQuery } = useFiltersStore();
  const [localQuery, setLocalQuery] = useState(searchQuery);
  const { trackEvent } = useMetricsEvents();

  // Track last query that was sent to analytics to prevent duplicate events
  const lastTrackedQueryRef = useRef<string | null>(null);

  // Compute validation state
  const trimmedQuery = useMemo(() => localQuery.trim(), [localQuery]);
  const sanitizedQueryWithoutLimit = useMemo(() => sanitizeSearchQuery(localQuery), [localQuery]);
  const sanitizedQuery = useMemo(
    () => sanitizedQueryWithoutLimit.slice(0, CAMPAIGN_SEARCH_QUERY_MAX_LENGTH),
    [sanitizedQueryWithoutLimit],
  );
  const hasInvalidChars = hasInvalidCharacters(localQuery, trimmedQuery);
  const exceedsLimit = exceedsMaxLength(sanitizedQueryWithoutLimit);
  const validationError = useMemo(
    () => getValidationError(localQuery, trimmedQuery, sanitizedQueryWithoutLimit),
    [localQuery, trimmedQuery, sanitizedQueryWithoutLimit],
  );

  // Debounce search query updates
  useEffect(() => {
    if (hasInvalidChars || exceedsLimit) {
      return;
    }

    const timer = setTimeout(() => {
      if (sanitizedQuery === searchQuery) {
        return;
      }

      setSearchQuery(sanitizedQuery);

      // Track search event only if different from last tracked query
      // This prevents duplicate events when user makes minor edits
      if (sanitizedQuery !== lastTrackedQueryRef.current) {
        if (sanitizedQuery) {
          trackEvent(METRICS_EVENTS.CAMPAIGNS_SEARCH, {
            searchQuery: sanitizedQuery,
            queryLength: sanitizedQuery.length,
            wasTruncated: sanitizedQuery.length !== sanitizedQueryWithoutLimit.length,
          });
        } else {
          trackEvent(METRICS_EVENTS.CAMPAIGNS_SEARCH_CLEARED, {});
        }
        lastTrackedQueryRef.current = sanitizedQuery;
      }
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [
    exceedsLimit,
    hasInvalidChars,
    sanitizedQuery,
    sanitizedQueryWithoutLimit.length,
    searchQuery,
    setSearchQuery,
    trackEvent,
  ]);

  // Sync with store when it changes externally (e.g., reset filters)
  useEffect(() => {
    setLocalQuery(searchQuery);
  }, [searchQuery]);

  return {
    localQuery,
    setLocalQuery,
    validationError,
  };
};
